import { useMemo } from "react";

import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { MountedWhileOpen } from "@/components/ui/mounted-while-open";
import { NameNoteDialog, type NameNoteValues } from "@/components/ui/name-note-dialog";
import { useDeleteSpecSelection, useUpdateSpecSelection } from "@/hooks/spec-editor-selections";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecLinesLine, SpecLinesSelection } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";

// The Editor's selection dialogs (#807) a selection row's ⋮ opens (#810) —
// `Edit Selection` and the `Delete Selection` confirm
// (`Lines/LineMoreActions/{EditSelectionDetails,DeleteDialog}.tsx`) — wired to
// #801's selection verbs. Like the line dialogs beside them: mounted only while
// open (#807 E19), awaited, closed on success, open with the `ERRORS` string on
// the shared `ErrorSnackbar` on a failure.

interface SelectionDialogProps {
  line: SpecLinesLine;
  selection: SpecLinesSelection;
  target: SpecEditorTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * `Edit Selection` (`EditSelectionDetails.tsx`): #805's name-and-note form
 * with `Clear Note` and `Save`, seeded from the selection whenever that object
 * changes. The X, Escape and the backdrop close it even mid-save.
 */
export function EditSpecSelectionDialog({ open, ...props }: SelectionDialogProps) {
  return (
    <MountedWhileOpen open={open}>
      {(onExited) => <EditSelectionForm open={open} onExited={onExited} {...props} />}
    </MountedWhileOpen>
  );
}

function EditSelectionForm({
  line,
  selection,
  target,
  open,
  onOpenChange,
  onExited,
}: SelectionDialogProps & { onExited: () => void }) {
  const update = useUpdateSpecSelection();
  // `useEffect([selection])` (`EditSelectionDetails.tsx:22-34`).
  const seed = useMemo<NameNoteValues>(
    () => ({ name: selection.name || "", notes: selection.notes || "" }),
    [selection],
  );

  return (
    <>
      <NameNoteDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Edit Selection"
        submitLabel="Save"
        saving={update.isPending}
        seed={seed}
        // `reset(…)` (`EditSelectionDetails.tsx:29-34`): a re-seed forgets a
        // tried submit.
        resetSubmitOnSeed
        onSubmit={(values) =>
          update.mutate(
            { ...target(), lineId: line.id, selectionId: selection.id, ...values },
            { onSuccess: () => onOpenChange(false) },
          )
        }
        onExited={onExited}
      />
      <ErrorSnackbar
        open={update.isError}
        message={ERRORS.UPDATE_SELECTION_DETAILS}
        onClose={update.reset}
      />
    </>
  );
}

/**
 * `Delete Selection` (`DeleteDialog.tsx`): the shared confirm with no clause —
 * the `?` and its non-breaking space, then the warning. A line's last selection
 * takes the line with it (server-side, #801). A success clears the Active
 * selection and then closes, as the line's does.
 */
export function DeleteSpecSelectionDialog({
  open,
  ...props
}: SelectionDialogProps & { onDeleted: () => void }) {
  return (
    <MountedWhileOpen open={open}>
      {(onExited) => <DeleteSelectionConfirm open={open} onExited={onExited} {...props} />}
    </MountedWhileOpen>
  );
}

function DeleteSelectionConfirm({
  line,
  selection,
  target,
  open,
  onOpenChange,
  onDeleted,
  onExited,
}: SelectionDialogProps & { onDeleted: () => void; onExited: () => void }) {
  const remove = useDeleteSpecSelection();

  return (
    <>
      <ConfirmDeleteDialog
        open={open}
        onOpenChange={onOpenChange}
        label="Selection"
        fullWidth={false}
        autoFocusConfirm={false}
        isDeleting={remove.isPending}
        onConfirm={() => {
          // The write's own promise, as the line's confirm: a dismissal
          // mid-delete must not lose the clear.
          remove.mutateAsync({ ...target(), lineId: line.id, selectionId: selection.id }).then(
            () => {
              onDeleted();
              onOpenChange(false);
            },
            // The failure is on the snackbar, through the hook's state.
            () => {},
          );
        }}
        onExited={onExited}
      />
      <ErrorSnackbar
        open={remove.isError}
        message={ERRORS.DELETE_SELECTION}
        onClose={remove.reset}
      />
    </>
  );
}
